import { useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Grid,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Button,
  Card,
  CardHeader,
  CardBody,
  Text,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Spinner,
  Flex,
  Tooltip,
  Icon,
  CircularProgress,
  CircularProgressLabel,
  SimpleGrid,
  useBoolean,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Avatar,
  Stack,
  ButtonGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Center
} from '@chakra-ui/react';
import {
  FiSettings,
  FiShield,
  FiLock,
  FiUnlock,
  FiDollarSign,
  FiCheckCircle,
  FiAlertTriangle,
  FiX,
  FiEye,
  FiEyeOff,
  FiZap,
  FiTrendingUp,
  FiTrendingDown,
  FiRefreshCw,
  FiSave,
  FiActivity,
  FiBarChart2,
  FiCpu,
  FiDatabase,
  FiWifi,
  FiWifiOff,
  FiAlertCircle,
  FiInfo,
  FiHelpCircle,
  FiPlay,
  FiPause,
  FiSliders,
  FiKey,
  FiTarget,
  FiLayers,
  FiPlus,
  FiMinus,
  FiCommand,
  FiFilter,
  FiCornerDownRight,
  FiClock
} from 'react-icons/fi';
import { useApiSettings, useUpdateApiSettings, useCheckConnection, useDisconnectApi, useDashboardData } from '../hooks/useApi';

// Enhanced animations
const floating = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const slideUp = keyframes`
  from { 
    transform: translateY(30px);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
`;

const glowPulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(34, 197, 94, 0.3);
  }
  50% { 
    box-shadow: 0 0 30px rgba(34, 197, 94, 0.6);
  }
`;

// Enhanced theme
const THEME = {
  glass: {
    primary: "rgba(15, 23, 42, 0.8)",
    secondary: "rgba(15, 23, 42, 0.6)",
    accent: "rgba(74, 222, 128, 0.1)"
  },
  borders: {
    primary: "rgba(74, 222, 128, 0.3)",
    active: "rgba(74, 222, 128, 0.6)",
    inactive: "rgba(255, 255, 255, 0.1)"
  },
  gradients: {
    success: "linear(135deg, #22c55e 0%, #10b981 100%)",
    info: "linear(135deg, #3b82f6 0%, #1d4ed8 100%)",
    warning: "linear(135deg, #f59e0b 0%, #d97706 100%)",
    danger: "linear(135deg, #ef4444 0%, #dc2626 100%)",
    neural: "linear(135deg, #4ade80 0%, #22d3ee 50%, #a78bfa 100%)"
  },
  shadows: {
    quantum: "0 8px 32px rgba(74, 222, 128, 0.2)",
    neural: "0 4px 16px rgba(0, 0, 0, 0.4)",
    inner: "inset 0 2px 4px rgba(0, 0, 0, 0.3)"
  }
};

const ApiSettings = () => {
  const toast = useToast();
  const { data: apiSettingsResponse, isLoading, isError, refetch } = useApiSettings();
  const { summary, stats, positions, refetch: refetchDashboard } = useDashboardData();
  const updateApiSettings = useUpdateApiSettings();
  const checkConnection = useCheckConnection();
  const disconnectApi = useDisconnectApi();
  
  // Form state
  const [credentials, setCredentials] = useState({
    api_key: '',
    api_secret: '',
    api_passphrase: '',
  });
  
  const [tradingConfig, setTradingConfig] = useState({
    enable_trading: false,
    secure_trade: true,
    max_usdt_margin: 5000,
    max_active_trades: 50,
    per_trade_usdt: 10,
    default_leverage: 20,
    margin_type: '1',
    trade_side: '0',
    trade_type: '99',
    airbag_threshold: 25.0,
  });
  
  // UI state
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isConfirmingDisconnect, setIsConfirmingDisconnect] = useState(false);
  const [showCredentials, setShowCredentials] = useBoolean(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [justConnected, setJustConnected] = useState(false);
  
  // Extract API data
  const apiSettings = apiSettingsResponse?.data?.data?.settings || {};
  const isConnected = apiSettings.api_connected || false;
  const isVerified = apiSettings.api_verified || false;
  const balanceData = summary?.summary || {};
  
  // Tab handling
  const handleTabChange = (index) => {
  if (!isConnected && index !== 0) {
    toast({
      title: '🔐 API Connection Required',
      description: 'Please enter and save your API credentials first.',
      status: 'warning',
      duration: 4000,
      isClosable: true,
    });
    return;
  }
  
  if (isConnected && !isVerified && index !== 0) {
    toast({
      title: '⚠️ API Verification Required',
      description: 'Please test your connection first.',
      status: 'warning',
      duration: 4000,
      isClosable: true,
    });
    return;
  }
  
  setSelectedTab(index);
};
  

useEffect(() => {
  // This effect runs once on component mount
  if (isInitialLoad && !isLoading && apiSettings) {
    // Check if API is connected and verified
    const isCurrentlyConnected = apiSettings.api_connected || false;
    const isCurrentlyVerified = apiSettings.api_verified || false;

    // If connected and verified, set tab to Control Center (index 2)
    if (isCurrentlyConnected && isCurrentlyVerified) {
      setSelectedTab(2); // Control Center tab
    }
    
    // Mark initial load as complete
    setIsInitialLoad(false);
  }
}, [isLoading, apiSettings, isInitialLoad]);




  // Initialize form
  useEffect(() => {
    if (apiSettings && Object.keys(apiSettings).length > 0) {
      setTradingConfig({
        enable_trading: apiSettings.enable_trading || false,
        secure_trade: true,
        max_usdt_margin: apiSettings.max_usdt_margin || 5000,
        max_active_trades: apiSettings.max_active_trades || 50,
        per_trade_usdt: apiSettings.per_trade_usdt || 10,
        default_leverage: apiSettings.default_leverage || 20,
        margin_type: apiSettings.margin_type?.toString() || '1',
        trade_side: apiSettings.trade_side?.toString() || '0',
        trade_type: apiSettings.trade_type?.toString() || '99',
        airbag_threshold: apiSettings.airbag !== undefined ? apiSettings.airbag : 25.0,
      });
    }
  }, [apiSettings]);
  
  // Track changes
  useEffect(() => {
    const hasCredentialChanges = credentials.api_key || credentials.api_secret || credentials.api_passphrase;
    const originalConfig = {
      enable_trading: apiSettings.enable_trading || false,
      max_usdt_margin: apiSettings.max_usdt_margin || 5000,
      max_active_trades: apiSettings.max_active_trades || 50,
      per_trade_usdt: apiSettings.per_trade_usdt || 10,
      default_leverage: apiSettings.default_leverage || 20,
      margin_type: apiSettings.margin_type?.toString() || '1',
      trade_side: apiSettings.trade_side?.toString() || '0',
      trade_type: apiSettings.trade_type?.toString() || '99',
      airbag_threshold: apiSettings.airbag !== undefined ? apiSettings.airbag : 25.0,
    };
    
    const currentConfig = { ...tradingConfig };
    delete currentConfig.secure_trade;
    
    const hasConfigChanges = JSON.stringify(currentConfig) !== JSON.stringify(originalConfig);
    setHasUnsavedChanges(hasCredentialChanges || hasConfigChanges);
  }, [credentials, tradingConfig, apiSettings]);
  
  // Handlers
  const handleCredentialChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
  };
  
  const handleConfigChange = (field, value) => {
    setTradingConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleTradingToggle = async () => {
  // Toggle the state immediately in UI for responsiveness
  handleConfigChange('enable_trading', !tradingConfig.enable_trading);
  
  // Save changes automatically
  setIsUpdating(true);
  
  try {
    const settings = { ...tradingConfig };
    settings.secure_trade = true;
    
    // Only sending the enable_trading parameter to reduce API payload
    const backendSettings = {
      enable_trading: !tradingConfig.enable_trading,
      secure_trade: true
    };
    
    const response = await updateApiSettings.mutateAsync(backendSettings);
    
    if (response.data?.success) {
      toast({
        title: tradingConfig.enable_trading ? '⚠️ Trading Stopped' : '✅ Trading Started',
        description: tradingConfig.enable_trading ? 
          'Trading has been disabled and settings saved.' : 
          'Trading has been enabled and settings saved.',
        status: tradingConfig.enable_trading ? 'info' : 'success',
        duration: 3000,
        isClosable: true,
      });
      
      await refetch();
    } else {
      throw new Error(response.data?.message || 'Failed to save settings');
    }
  } catch (error) {
    // Revert UI state if save fails
    handleConfigChange('enable_trading', tradingConfig.enable_trading);
    
    toast({
      title: '❌ Toggle Failed',
      description: error.message || 'Please try again.',
      status: 'error',
      duration: 4000,
      isClosable: true,
    });
  } finally {
    setIsUpdating(false);
  }
};
  
  // Save handler
  const handleSave = async () => {
    setIsUpdating(true);
    
    try {
      const settings = { ...tradingConfig };
      settings.secure_trade = true;
      
      const backendSettings = {
        enable_trading: settings.enable_trading,
        secure_trade: settings.secure_trade,
        max_total_capital: settings.max_usdt_margin === 999999 ? 999999 : settings.max_usdt_margin,
        per_trade_margin: settings.per_trade_usdt,
        max_active_trades: settings.max_active_trades === 999 ? 999 : settings.max_active_trades,
        default_leverage: settings.default_leverage,
        margin_type: parseInt(settings.margin_type),
        trade_side: parseInt(settings.trade_side),
        trade_type: parseInt(settings.trade_type),
        airbag: settings.airbag_threshold,
      };
      
      if (credentials.api_key) backendSettings.api_key = credentials.api_key;
      if (credentials.api_secret) backendSettings.api_secret = credentials.api_secret;
      if (credentials.api_passphrase) backendSettings.api_passphrase = credentials.api_passphrase;
      
      const response = await updateApiSettings.mutateAsync(backendSettings);
      
      if (response.data?.success) {
        toast({
          title: '✅ Settings Saved',
          description: 'Configuration updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setCredentials({ api_key: '', api_secret: '', api_passphrase: '' });
        await refetch();
      } else {
        throw new Error(response.data?.message || 'Failed to save settings');
      }
    } catch (error) {
      toast({
        title: '❌ Save Failed',
        description: error.message || 'Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Connection test handler
  const handleTestConnection = async () => {
    setIsChecking(true);
    
    try {
      const response = await checkConnection.mutateAsync();
      
      if (response.data?.success) {
        toast({
          title: '✅ Connection Verified',
          description: 'API connection successful.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setJustConnected(true);
        await refetch();
        await refetchDashboard();
      } else {
        throw new Error(response.data?.message || 'Connection test failed');
      }
    } catch (error) {
      toast({
        title: '❌ Connection Failed',
        description: error.message || 'Please check your credentials.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsChecking(false);
    }
  };
  
  // Disconnect handler
  const handleDisconnect = async () => {
    if (!isConfirmingDisconnect) {
      setIsConfirmingDisconnect(true);
      
      toast({
        title: '⚠️ Disconnect API?',
        description: 'This will stop all trading activities.',
        status: 'warning',
        duration: 6000,
        isClosable: false,
        render: ({ onClose }) => (
          <Alert
            status="warning"
            borderRadius="md"
            bg="rgba(15, 27, 14, 0.8)"
            borderWidth="1px"
            borderColor="orange.400"
          >
            <AlertIcon />
            <Box flex="1">
              <Text fontWeight="bold">Disconnect API?</Text>
              <Text fontSize="sm" mt={1}>This will stop all trading activities.</Text>
              <HStack spacing={2} mt={3}>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsConfirmingDisconnect(false);
                    onClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="red"
                  onClick={() => {
                    setIsConfirmingDisconnect(false);
                    onClose();
                    performDisconnect();
                  }}
                >
                  Disconnect
                </Button>
              </HStack>
            </Box>
          </Alert>
        )
      });
      
      setTimeout(() => {
        setIsConfirmingDisconnect(false);
      }, 6000);
      
      return;
    }
  };
  
  const performDisconnect = async () => {
    setIsDisconnecting(true);
    
    try {
      const response = await disconnectApi.mutateAsync();
      
      if (response.data?.success) {
        toast({
          title: '🔒 API Disconnected',
          description: 'All trading stopped successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setCredentials({ api_key: '', api_secret: '', api_passphrase: '' });
        setSelectedTab(0);
        await refetch();
        await refetchDashboard();
      } else {
        throw new Error(response.data?.message || 'Failed to disconnect');
      }
    } catch (error) {
      toast({
        title: '❌ Disconnect Failed',
        description: error.message || 'Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsDisconnecting(false);
    }
  };
  
  // Utility functions
  const maskApiKey = (key, isConnected = false) => {
    if (!key && !isConnected) return '';
    const displayKey = key || 'bg_1234567890abcdef1234567890abcdef';
    if (displayKey.length <= 8) return displayKey;
    return `${displayKey.substring(0, 6)}${'•'.repeat(Math.max(0, displayKey.length - 12))}${displayKey.substring(displayKey.length - 6)}`;
  };
  
  const isAirbagOff = (value) => value === -9999;
  const getAirbagDisplayValue = (value) => isAirbagOff(value) ? 'Off' : `${value}%`;
  
  const isMaxTradesUnlimited = (value) => value === 999;
  const getMaxTradesDisplayValue = (value) => isMaxTradesUnlimited(value) ? 'Unlimited' : value.toString();
  
  const isTotalCapitalUnlimited = (value) => value === 999999;
  const getTotalCapitalDisplayValue = (value) => isTotalCapitalUnlimited(value) ? 'Unlimited' : `${value} USDT`;
  const getTotalCapitalServerValue = (value) => isTotalCapitalUnlimited(value) ? 500000 : value;
  
  // Calculate trade capacity
  const calculateTradeCapacity = () => {
  // Safely extract values from API data, providing fallbacks
  const totalBalance = parseFloat(summary?.totalBalance) || 0;
  const availableBalance = parseFloat(summary?.availableBalance) || 0;
  const unrealizedPnl = parseFloat(summary?.unrealizedPnl) || 0;
  const totalPnlPercentage = parseFloat(summary?.totalPnlPercentage) || 0;
  const marginUsed = parseFloat(summary?.marginUsed) || 0;
  
  // Calculate max exposure based on trading config
  const tradesForCalculation = isMaxTradesUnlimited(tradingConfig.max_active_trades) ? 300 : tradingConfig.max_active_trades;
  const perTradeAmount = parseFloat(tradingConfig.per_trade_usdt) || 10;
  const maxCapital = getTotalCapitalServerValue(tradingConfig.max_usdt_margin);
  
  // Maximum potential exposure based on settings
  const maxExposure = perTradeAmount * tradesForCalculation;
  
  // Calculate the utilization percentage
  // This shows how much of the total balance would be used if all allowed trades were opened
  const utilizationPercent = totalBalance > 0 ? (maxExposure / totalBalance) * 100 : 0;
  
  // Check if user has enough available balance for at least one trade
  const canTrade = availableBalance >= perTradeAmount;
  
  return {
    totalBalance,
    availableBalance,
    unrealizedPnl,
    totalPnlPercentage,
    marginUsed,
    maxExposure,
    maxCapital,
    utilizationPercent: Math.min(utilizationPercent, 100), // Cap at 100%
    canTrade
  };
};
  
  const tradeCapacity = calculateTradeCapacity();
  
  // Loading state
  if (isLoading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="green.400" thickness="4px" />
          <Text color="gray.400">Loading API settings...</Text>
        </VStack>
      </Center>
    );
  }
  
  // Error state
  if (isError) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Icon as={FiAlertTriangle} boxSize={16} color="red.400" />
          <VStack spacing={2} textAlign="center">
            <Heading size="md" color="red.400">Failed to Load Settings</Heading>
            <Text color="gray.400">Please check your connection and try again.</Text>
          </VStack>
          <Button onClick={() => window.location.reload()} colorScheme="red" variant="outline">
            Refresh Page
          </Button>
        </VStack>
      </Center>
    );
  }
  
  return (
    <>
   
      
      <Box className="api-settings-container" animation={`${slideUp} 0.6s ease-out`}>
        <VStack spacing={6} align="stretch" maxW="100%" overflow="hidden">
          {/* Header */}
          <Flex 
            direction={{ base: "column", sm: "row" }} 
            justify="space-between" 
            align={{ base: "stretch", sm: "center" }} 
            gap={4}
            mb={4}
          >
            <Heading size="lg" display="flex" alignItems="center">
              <Icon as={FiSettings} mr={2} color="green.400" />
              API Settings
            </Heading>
            
            <HStack spacing={4} flexShrink={0} w={{ base: "full", sm: "auto" }}>
              {/* Connection Status Badge */}
              <Badge
                px={4}
                py={2}
                borderRadius="full"
                colorScheme={isConnected && isVerified ? 'green' : isConnected ? 'yellow' : 'red'}
                fontSize="sm"
                fontWeight="bold"
                flexShrink={0}
              >
                <HStack spacing={2}>
                  <Icon as={isConnected ? (isVerified ? FiCheckCircle : FiClock) : FiWifiOff} />
                  <Text display={{ base: "none", sm: "inline" }}>
                    {isConnected && isVerified ? 'Fully Operational' : 
                     isConnected ? 'Connected (Unverified)' : 'Disconnected'}
                  </Text>
                  <Text display={{ base: "inline", sm: "none" }}>
                    {isConnected && isVerified ? 'Active' : 
                     isConnected ? 'Unverified' : 'Offline'}
                  </Text>
                </HStack>
              </Badge>
              
              {/* Save Button */}
              <Button
                onClick={handleSave}
                leftIcon={<FiSave />}
                colorScheme="green"
                size="sm"
                borderRadius="md"
                isDisabled={!hasUnsavedChanges}
                isLoading={isUpdating}
                _hover={{
                  transform: "translateY(-1px)",
                }}
                transition="all 0.2s"
                animation={hasUnsavedChanges ? `${glowPulse} 3s ease-in-out infinite` : 'none'}
                flexShrink={0}
              >
                <Text display={{ base: "none", sm: "inline" }}>Save Changes</Text>
                <Text display={{ base: "inline", sm: "none" }}>Save</Text>
              </Button>
            </HStack>
          </Flex>
          
          {/* Unsaved Changes Warning */}
          {hasUnsavedChanges && (
            <Flex justify="center" animation={`${slideUp} 0.6s ease-out`}>
              <Badge
                px={4}
                py={2}
                borderRadius="full"
                colorScheme="orange"
                fontSize="sm"
                fontWeight="bold"
                animation={`${glowPulse} 2s ease-in-out infinite`}
              >
                <HStack spacing={2}>
                  <Icon as={FiAlertCircle} />
                  <Text>You have unsaved changes</Text>
                </HStack>
              </Badge>
            </Flex>
          )}
          
          {/* Enhanced Tabs */}
          <Tabs 
            index={selectedTab} 
            onChange={handleTabChange}
            variant="enclosed"
            colorScheme="green"
          >
            <Box overflowX="auto" pb={2}>
              <TabList
                bg='rgba(15, 27, 14, 0.8)'
                borderRadius="xl"
                p={2}
                border="1px solid"
                borderColor={THEME.borders.primary}
                backdropFilter="blur(20px)"
                animation={`${slideUp} 0.8s ease-out`}
                minW="fit-content"
                justifyContent={{ base: "center", md: "flex-start" }}
              >
                <Tab
                  flex={{ base: "none", md: "1" }}
                  minW={{ base: "80px", md: "120px" }}
                  _selected={{
                    bg: THEME.gradients.info,
                    color: 'white',
                    fontWeight: 'bold',
                    borderWidth: '2px',
                    borderColor: 'blue.400',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
                    transform: 'translateY(-2px)'
                  }}
                  borderRadius="lg"
                  _hover={{ transform: 'translateY(-2px)' }}
                  transition="all 0.3s"
                  border="2px solid transparent"
                  mx={1}
                  py={{ base: 3, md: 2 }}
                >
                  <VStack spacing={{ base: 1, md: 2 }} display={{ base: "flex", md: "none" }}>
                    <Icon as={FiLock} color={selectedTab === 0 ? 'white' : 'blue.400'} boxSize={{ base: 4, md: 4 }} />
                    <Text fontSize="xs" color={selectedTab === 0 ? 'white' : 'gray.300'} textAlign="center">
                      Security
                    </Text>
                    {selectedTab === 0 && !isConnected && (
                      <Box w="6px" h="6px" bg="red.400" borderRadius="full" />
                    )}
                    {selectedTab === 0 && isConnected && !isVerified && (
                      <Box w="6px" h="6px" bg="yellow.400" borderRadius="full" />
                    )}
                  </VStack>
                  
                  <HStack spacing={2} display={{ base: "none", md: "flex" }}>
                    <Icon as={FiLock} color={selectedTab === 0 ? 'white' : 'blue.400'} />
                    <Text color={selectedTab === 0 ? 'white' : 'gray.300'}>Security</Text>
                    {selectedTab === 0 && !isConnected && (
                      <Badge colorScheme="red" size="sm" borderRadius="full">Setup</Badge>
                    )}
                    {selectedTab === 0 && isConnected && !isVerified && (
                      <Badge colorScheme="yellow" size="sm" borderRadius="full">Verify</Badge>
                    )}
                  </HStack>
                </Tab>
                
                <Tab
                  flex={{ base: "none", md: "1" }}
                  minW={{ base: "80px", md: "120px" }}
                  _selected={{
                    bg: THEME.gradients.success,
                    color: 'white',
                    fontWeight: 'bold',
                    borderWidth: '2px',
                    borderColor: 'green.400',
                    boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)',
                    transform: 'translateY(-2px)'
                  }}
                  borderRadius="lg"
                  _hover={{ transform: 'translateY(-2px)' }}
                  transition="all 0.3s"
                  border="2px solid transparent"
                  isDisabled={!isConnected || !isVerified}
                  opacity={(!isConnected || !isVerified) ? 0.5 : 1}
                  cursor={(!isConnected || !isVerified) ? 'not-allowed' : 'pointer'}
                  mx={1}
                  py={{ base: 3, md: 2 }}
                >
                  <VStack spacing={{ base: 1, md: 2 }} display={{ base: "flex", md: "none" }}>
                    <Icon as={FiSliders} color={selectedTab === 1 ? 'white' : 'green.400'} boxSize={{ base: 4, md: 4 }} />
                    <Text fontSize="xs" color={selectedTab === 1 ? 'white' : 'gray.300'} textAlign="center">
                      Trading
                    </Text>
                    {(!isConnected || !isVerified) && (
                      <Box w="6px" h="6px" bg="red.400" borderRadius="full" />
                    )}
                  </VStack>
                  
                  <HStack spacing={2} display={{ base: "none", md: "flex" }}>
                    <Icon as={FiSliders} color={selectedTab === 1 ? 'white' : 'green.400'} />
                    <Text color={selectedTab === 1 ? 'white' : 'gray.300'}>Trading</Text>
                    {(!isConnected || !isVerified) && <Icon as={FiLock} boxSize={3} color="red.400" />}
                  </HStack>
                </Tab>
                
                <Tab
                  flex={{ base: "none", md: "1" }}
                  minW={{ base: "80px", md: "120px" }}
                  _selected={{
                    bg: THEME.gradients.warning,
                    color: 'white',
                    fontWeight: 'bold',
                    borderWidth: '2px',
                    borderColor: 'orange.400',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
                    transform: 'translateY(-2px)'
                  }}
                  borderRadius="lg"
                  _hover={{ transform: 'translateY(-2px)' }}
                  transition="all 0.3s"
                  border="2px solid transparent"
                  isDisabled={!isConnected || !isVerified}
                  opacity={(!isConnected || !isVerified) ? 0.5 : 1}
                  cursor={(!isConnected || !isVerified) ? 'not-allowed' : 'pointer'}
                  mx={1}
                  py={{ base: 3, md: 2 }}
                >
                  <VStack spacing={{ base: 1, md: 2 }} display={{ base: "flex", md: "none" }}>
                    <Icon as={FiActivity} color={selectedTab === 2 ? 'white' : 'orange.400'} boxSize={{ base: 4, md: 4 }} />
                    <Text fontSize="xs" color={selectedTab === 2 ? 'white' : 'gray.300'} textAlign="center">
                      Control
                    </Text>
                   
                  </VStack>
                  
                  <HStack spacing={2} display={{ base: "none", md: "flex" }}>
                    <Icon as={FiActivity} color={selectedTab === 2 ? 'white' : 'orange.400'} />
                    <Text color={selectedTab === 2 ? 'white' : 'gray.300'}>Control Center</Text>
                  
                  </HStack>
                </Tab>
              </TabList>
            </Box>
            
            <TabPanels mt={6}>
              {/* Security Tab */}
              <TabPanel p={0}>
                <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
                  {/* API Credentials */}
                  <Card
                    bg='rgba(15, 27, 14, 0.8)'
                    borderWidth="1px"
                    borderColor={THEME.borders.primary}
                    borderRadius="xl"
                    backdropFilter="blur(20px)"
                    overflow="hidden"
                    animation={`${slideUp} 0.6s ease-out 0.1s both`}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
                    transition="all 0.3s"
                  >
                    <CardHeader
                      bg="rgba(15, 27, 14, 0.8)"
                      borderBottomWidth="1px"
                      borderColor={THEME.borders.inactive}
                    >
                      <HStack justify="space-between">
                        <HStack spacing={3}>
                          <Avatar 
                            size="sm" 
                            bg="blue.500" 
                            icon={<FiKey color="white" />} 
                            animation={`${floating} 4s ease-in-out infinite`}
                          />
                          <VStack align="start" spacing={0}>
                            <Heading size="md" color="white">API Credentials</Heading>
                            <Text fontSize="sm" color="gray.400">Bitget Exchange Connection</Text>
                          </VStack>
                        </HStack>
                        
                        <IconButton
                          aria-label="Toggle visibility"
                          icon={showCredentials ? <FiEyeOff /> : <FiEye />}
                          size="sm"
                          variant="ghost"
                          onClick={setShowCredentials.toggle}
                          _hover={{ transform: 'rotate(15deg)' }}
                          transition="all 0.3s"
                        />
                      </HStack>
                    </CardHeader>
                    
                    <CardBody>
                      <VStack spacing={5} align="stretch">
                        {isConnected && (
                          <Alert 
                            status={isVerified ? "success" : "info"} 
                            borderRadius="lg" 
                           
                          >
                            <AlertIcon />
                            <AlertDescription>
                              {isVerified ? 
                                "API connected and verified. Leave fields empty to keep current credentials." :
                                "API connected but not verified. Test connection to enable full access."
                              }
                            </AlertDescription>
                          </Alert>
                        )}
                        
                       <FormControl>
  <FormLabel>
    <HStack>
      <Icon as={FiDatabase} color="green.400" />
      <Text>API Key</Text>
      <Tooltip 
        label="Your Bitget API key with futures trading permissions"
        hasArrow
        placement="top"
        closeOnClick={true}
        closeOnMouseDown={true}
        openDelay={200}
        closeDelay={100}
        shouldWrapChildren
        portalProps={{ appendToParentPortal: false }}
        gutter={8}
        zIndex={9999}
      >
        <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
      </Tooltip>
    </HStack>
  </FormLabel>
  <Input
    type={showCredentials ? "text" : "password"}
    value={credentials.api_key}
    onChange={(e) => handleCredentialChange('api_key', e.target.value)}
    placeholder={isConnected ? `Connected: ${apiSettings.api_key_masked || '••••••••••••••••'}` : "bg_1234567890abcdef..."}
    bg="rgba(0, 0, 0, 0.4)"
    borderColor="rgba(255, 255, 255, 0.1)"
    _focus={{ borderColor: "green.400" }}
    _hover={{ borderColor: "green.300" }}
    _placeholder={{
      color: isConnected ? "green.400" : "gray.500"
    }}
    transition="all 0.3s"
  />
</FormControl>
                        
                       <FormControl>
  <FormLabel>
    <HStack>
      <Icon as={FiShield} color="red.400" />
      <Text>API Secret</Text>
      <Tooltip 
        label="Keep this secret secure - never share it publicly"
        hasArrow
        placement="top"
        closeOnClick={true}
        closeOnMouseDown={true}
        openDelay={200}
        closeDelay={100}
        shouldWrapChildren
        portalProps={{ appendToParentPortal: false }}
        gutter={8}
        zIndex={9999}
      >
        <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
      </Tooltip>
    </HStack>
  </FormLabel>
  <Input
    type={showCredentials ? "text" : "password"}
    value={credentials.api_secret}
    onChange={(e) => handleCredentialChange('api_secret', e.target.value)}
    placeholder={isConnected ? `Connected: ${apiSettings.api_secret_masked || '•••••••••••••••••••••••••••••••••••••••••••••••••••••'}` : "Enter your API secret"}
    bg="rgba(0, 0, 0, 0.4)"
    borderColor="rgba(255, 255, 255, 0.1)"
    _focus={{ borderColor: "red.400" }}
    _hover={{ borderColor: "red.300" }}
    _placeholder={{
      color: isConnected ? "green.400" : "gray.500"
    }}
    transition="all 0.3s"
  />
</FormControl>
                        
                      <FormControl>
  <FormLabel>
    <HStack>
      <Icon as={FiUnlock} color="purple.400" />
      <Text>API Passphrase</Text>
      <Tooltip 
        label="Your custom passphrase set during API creation"
        hasArrow
        placement="top"
        closeOnClick={true}
        closeOnMouseDown={true}
        openDelay={200}
        closeDelay={100}
        shouldWrapChildren
        portalProps={{ appendToParentPortal: false }}
        gutter={8}
        zIndex={9999}
      >
        <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
      </Tooltip>
    </HStack>
  </FormLabel>
  <Input
    type={showCredentials ? "text" : "password"}
    value={credentials.api_passphrase}
    onChange={(e) => handleCredentialChange('api_passphrase', e.target.value)}
    placeholder={isConnected ? `Connected: ${apiSettings.api_passphrase_masked || '••••••••••••••••••••••••••••••••'}` : "Enter your API passphrase"}
    bg="rgba(0, 0, 0, 0.4)"
    borderColor="rgba(255, 255, 255, 0.1)"
    _focus={{ borderColor: "purple.400" }}
    _hover={{ borderColor: "purple.300" }}
    _placeholder={{
      color: isConnected ? "green.400" : "gray.500"
    }}
    transition="all 0.3s"
  />
</FormControl>
                        
                        <Divider />
                        
                        <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                          <Button
                            onClick={handleSave}
                            colorScheme="green"
                            leftIcon={<FiSave />}
                            isLoading={isUpdating}
                            isDisabled={!credentials.api_key && !credentials.api_secret && !credentials.api_passphrase && !hasUnsavedChanges}
                            flex="1"
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                            transition="all 0.3s"
                          >
                            Save Credentials
                          </Button>
                          
                          <Button
                            onClick={handleTestConnection}
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<FiCheckCircle />}
                            isLoading={isChecking}
                            isDisabled={!isConnected}
                            _hover={{ transform: 'translateY(-2px)' }}
                            transition="all 0.3s"
                            flex={{ base: "1", sm: "auto" }}
                          >
                            Test Connection
                          </Button>
                        </Stack>
                        
                        {/* Connection Tips */}
                        {!isConnected && (
                          <Box p={3} bg="rgba(15, 27, 14, 0.8)" borderRadius="lg" border="1px solid" borderColor="blue.400">
                            <Text fontSize="sm" color="blue.300" fontWeight="medium" mb={2}>
                              💡 Setup Tips:
                            </Text>
                            <VStack spacing={1} align="start" fontSize="xs" color="blue.200">
                              <Text>• Enable futures trading permissions on your API</Text>
                              <Text>• Do not enable withdraw permissions for security</Text>
                              <Text>• Keep your secret and passphrase secure</Text>
                              <Text>• Test connection after saving credentials</Text>
                            </VStack>
                          </Box>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                  
                  {/* Enhanced Connection Status */}
                  <Card
                    bg='rgba(15, 27, 14, 0.8)'
                    borderWidth="1px"
                    borderColor={THEME.borders.primary}
                    borderRadius="xl"
                    backdropFilter="blur(20px)"
                    overflow="hidden"
                    animation={`${slideUp} 0.6s ease-out 0.2s both`}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
                    transition="all 0.3s"
                  >
                    <CardBody>
                      <Center h="full">
                        <VStack spacing={8}>
                          <Box position="relative">
                            <CircularProgress
                              value={isConnected && isVerified ? 100 : isConnected ? 70 : 0}
                              color={isConnected && isVerified ? "green.400" : isConnected ? "yellow.400" : "red.400"}
                              size="160px"
                              thickness="8px"
                              animation={isConnected && isVerified ? `${floating} 3s ease-in-out infinite` : 'none'}
                            >
                              <CircularProgressLabel>
                                <Icon 
                                  as={isConnected && isVerified ? FiCheckCircle : isConnected ? FiClock : FiWifiOff} 
                                  boxSize={14}
                                  color={isConnected && isVerified ? "green.400" : isConnected ? "yellow.400" : "red.400"}
                                  animation={isConnected && isVerified ? `${floating} 3s ease-in-out infinite` : 'none'}
                                />
                              </CircularProgressLabel>
                            </CircularProgress>
                            
                            {/* Status Ring Effect */}
                            {isConnected && isVerified && (
                              <Box
                                position="absolute"
                                top="-5px"
                                left="-5px"
                                right="-5px"
                                bottom="-5px"
                                borderRadius="full"
                                border="2px solid"
                                borderColor="green.400"
                                opacity="0.3"
                              />
                            )}
                          </Box>
                          
                          <VStack spacing={3} textAlign="center">
                            <Heading size="lg" color="white">Connection Status</Heading>
                            <Badge
                              px={6}
                              py={2}
                              borderRadius="full"
                              colorScheme={isConnected && isVerified ? "green" : isConnected ? "yellow" : "red"}
                              fontSize="md"
                              fontWeight="bold"
                            
                            >
                              {isConnected && isVerified ? "FULLY OPERATIONAL" :
                               isConnected ? "CONNECTED • NOT VERIFIED" : "NOT CONNECTED"}
                            </Badge>
                            
                            {/* Status Details */}
                            <VStack spacing={2} mt={4}>
                              <HStack>
                                <Icon as={FiDatabase} color={isConnected ? "green.400" : "gray.400"} />
                                <Text fontSize="sm" color={isConnected ? "white" : "gray.400"}>
                                  API Connection: {isConnected ? "Active" : "Inactive"}
                                </Text>
                              </HStack>
                              <HStack>
                                <Icon as={FiShield} color={isVerified ? "green.400" : "gray.400"} />
                                <Text fontSize="sm" color={isVerified ? "white" : "gray.400"}>
                                  Verification: {isVerified ? "Verified" : "Pending"}
                                </Text>
                              </HStack>
                              <HStack>
                                <Icon as={FiActivity} color={tradingConfig.enable_trading ? "green.400" : "gray.400"} />
                                <Text fontSize="sm" color={tradingConfig.enable_trading ? "white" : "gray.400"}>
                                  Trading: {tradingConfig.enable_trading ? "Enabled" : "Disabled"}
                                </Text>
                              </HStack>
                            </VStack>
                          </VStack>
                          
                          {/* Action Buttons */}
                          <VStack spacing={3} w="full">
                            {!isConnected && (
                              <Text fontSize="sm" color="gray.400" textAlign="center">
                                Enter your API credentials above to get started
                              </Text>
                            )}
                            
                            {isConnected && !isVerified && (
                              <Button
                                onClick={handleTestConnection}
                                colorScheme="blue"
                                leftIcon={<FiCheckCircle />}
                                isLoading={isChecking}
                                size="lg"
                                _hover={{ transform: 'scale(1.05)' }}
                                transition="all 0.3s"
                                w="full"
                              >
                                Verify Connection
                              </Button>
                            )}
                            
                            {isConnected && isVerified && (
                              <Button
                                onClick={handleDisconnect}
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FiX />}
                                isLoading={isDisconnecting}
                                size="md"
                                _hover={{ transform: 'scale(1.05)' }}
                                transition="all 0.3s"
                              >
                                Disconnect API
                              </Button>
                            )}
                          </VStack>
                        </VStack>
                      </Center>
                    </CardBody>
                  </Card>
                </Grid>
              </TabPanel>
              
              {/* Trading Tab */}
              <TabPanel p={0}>
                <VStack spacing={6} align="stretch">
                  {/* Trading Controls */}
                  <Card
                    bg='rgba(15, 27, 14, 0.8)'
                    borderWidth="1px"
                    borderColor={THEME.borders.primary}
                    borderRadius="xl"
                    backdropFilter="blur(20px)"
                    overflow="hidden"
                    animation={`${slideUp} 0.6s ease-out 0.1s both`}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
                    transition="all 0.3s"
                  >
                    <CardHeader
                      bg="rgba(0, 0, 0, 0.3)"
                      borderBottomWidth="1px"
                      borderColor={THEME.borders.inactive}
                    >
                      <HStack spacing={3}>
                        <Avatar 
                          size="sm" 
                          bg="purple.500" 
                          icon={<FiSliders color="white" />} 
                          animation={`${floating} 5s ease-in-out infinite`}
                        />
                        <VStack align="start" spacing={0}>
                          <Heading size="md" color="white">Trading Strategy</Heading>
                          <Text fontSize="sm" color="gray.400">Configure AI trading parameters</Text>
                        </VStack>
                      </HStack>
                    </CardHeader>
                    
                    <CardBody>
                      <VStack spacing={6} align="stretch">
                        {/* Strategy Selection */}
                        <FormControl>
                          <FormLabel>
                            <HStack>
                              <Icon as={FiCpu} color="blue.400" />
                              <Text>AI Model</Text>
                              <Tooltip 
                                label="Choose the AI model for market analysis and trading decisions"
                                hasArrow
                                placement="top"
                                closeOnClick={true}
                                closeOnMouseDown={true}
                                openDelay={200}
                                closeDelay={100}
                                shouldWrapChildren
                                portalProps={{ appendToParentPortal: false }}
                                gutter={8}
                                zIndex={9999}
                              >
                                <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                              </Tooltip>
                            </HStack>
                          </FormLabel>
                          <ButtonGroup isAttached variant="outline" w="full" className="mobile-button-group">
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.trade_type === '4' ? 'blue' : 'gray'}
                              variant={tradingConfig.trade_type === '4' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('trade_type', '4')}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              Model 4
                            </Button>
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.trade_type === '8' ? 'purple' : 'gray'}
                              variant={tradingConfig.trade_type === '8' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('trade_type', '8')}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              Model 8
                            </Button>
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.trade_type === '99' ? 'green' : 'gray'}
                              variant={tradingConfig.trade_type === '99' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('trade_type', '99')}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              Auto Model
                            </Button>
                          </ButtonGroup>
                        </FormControl>
                        
                        {/* Trading Direction */}
                        <FormControl>
                          <FormLabel>
                            <HStack>
                              <Icon as={FiBarChart2} color="green.400" />
                              <Text>Trading Direction</Text>
                              <Tooltip 
                                label="Choose which market directions the bot can trade"
                                hasArrow
                                placement="top"
                                closeOnClick={true}
                                closeOnMouseDown={true}
                                openDelay={200}
                                closeDelay={100}
                                shouldWrapChildren
                                portalProps={{ appendToParentPortal: false }}
                                gutter={8}
                                zIndex={9999}
                              >
                                <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                              </Tooltip>
                            </HStack>
                          </FormLabel>
                          <ButtonGroup isAttached variant="outline" w="full" className="mobile-button-group">
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.trade_side === '1' ? 'green' : 'gray'}
                              variant={tradingConfig.trade_side === '1' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('trade_side', '1')}
                              leftIcon={<FiTrendingUp />}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              <Text display={{ base: "none", sm: "inline" }}>Long Only</Text>
                              <Text display={{ base: "inline", sm: "none" }}>Long</Text>
                            </Button>
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.trade_side === '2' ? 'red' : 'gray'}
                              variant={tradingConfig.trade_side === '2' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('trade_side', '2')}
                              leftIcon={<FiTrendingDown />}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              <Text display={{ base: "none", sm: "inline" }}>Short Only</Text>
                              <Text display={{ base: "inline", sm: "none" }}>Short</Text>
                            </Button>
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.trade_side === '0' ? 'blue' : 'gray'}
                              variant={tradingConfig.trade_side === '0' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('trade_side', '0')}
                              leftIcon={<FiActivity />}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              Both
                            </Button>
                          </ButtonGroup>
                        </FormControl>
                        
                        {/* Margin Type */}
                        <FormControl>
                          <FormLabel>
                            <HStack>
                              <Icon as={FiShield} color="orange.400" />
                              <Text>Margin Type</Text>
                              <Tooltip 
                                label="Isolated: Risk limited to position margin. Cross: Uses available account balance"
                                hasArrow
                                placement="top"
                                closeOnClick={true}
                                closeOnMouseDown={true}
                                openDelay={200}
                                closeDelay={100}
                                shouldWrapChildren
                                portalProps={{ appendToParentPortal: false }}
                                gutter={8}
                                zIndex={9999}
                              >
                                <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                              </Tooltip>
                            </HStack>
                          </FormLabel>
                          <ButtonGroup isAttached variant="outline" w="full" className="mobile-button-group">
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.margin_type === '1' ? 'green' : 'gray'}
                              variant={tradingConfig.margin_type === '1' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('margin_type', '1')}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              <Text display={{ base: "none", sm: "inline" }}>Isolated (Recommended)</Text>
                              <Text display={{ base: "inline", sm: "none" }}>Isolated</Text>
                            </Button>
                            <Button
                              flex="1"
                              colorScheme={tradingConfig.margin_type === '2' ? 'green' : 'gray'}
                              variant={tradingConfig.margin_type === '2' ? 'solid' : 'outline'}
                              onClick={() => handleConfigChange('margin_type', '2')}
                              _hover={{ transform: 'scale(1.02)' }}
                              transition="all 0.2s"
                            >
                              Cross
                            </Button>
                          </ButtonGroup>
                        </FormControl>
                      </VStack>
                    </CardBody>
                  </Card>
                  
                  {/* Risk Management */}
                  <Card
                    bg='rgba(15, 27, 14, 0.8)'
                    borderWidth="1px"
                    borderColor={THEME.borders.primary}
                    borderRadius="xl"
                    backdropFilter="blur(20px)"
                    overflow="hidden"
                    animation={`${slideUp} 0.6s ease-out 0.2s both`}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
                    transition="all 0.3s"
                  >
                    <CardHeader
                      bg="rgba(0, 0, 0, 0.3)"
                      borderBottomWidth="1px"
                      borderColor={THEME.borders.inactive}
                    >
                      <HStack spacing={3}>
                        <Avatar 
                          size="sm" 
                          bg="red.500" 
                          icon={<FiShield color="white" />} 
                          animation={`${floating} 4s ease-in-out infinite`}
                        />
                        <VStack align="start" spacing={0}>
                          <Heading size="md" color="white">Risk Management</Heading>
                          <Text fontSize="sm" color="gray.400">Position limits and protection settings</Text>
                        </VStack>
                      </HStack>
                    </CardHeader>
                    
                    <CardBody>
                      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
                        <VStack spacing={6} align="stretch">
                          {/* Total Capital (USDT) with Unlimited option */}
                          <FormControl>
                            <FormLabel>
                              <HStack>
                                <Icon as={FiDollarSign} color="green.400" />
                                <Text>Total Capital (USDT)</Text>
                                <Tooltip 
                                  label="Maximum capital available for all trading positions combined"
                                  hasArrow
                                  placement="top"
                                  closeOnClick={true}
                                  closeOnMouseDown={true}
                                  openDelay={200}
                                  closeDelay={100}
                                  shouldWrapChildren
                                  portalProps={{ appendToParentPortal: false }}
                                  gutter={8}
                                  zIndex={9999}
                                >
                                  <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                                </Tooltip>
                              </HStack>
                            </FormLabel>
                            
                            {isTotalCapitalUnlimited(tradingConfig.max_usdt_margin) ? (
                              <Button
                                colorScheme="green"
                                variant="outline"
                                onClick={() => handleConfigChange('max_usdt_margin', 5000)}
                                w="full"
                                _hover={{ transform: 'scale(1.05)' }}
                                transition="all 0.3s"
                              >
                                Set Limit
                              </Button>
                            ) : (
                              <VStack spacing={3}>
                                <HStack w="full" className="mobile-input-group">
                                  <IconButton
                                    aria-label="Decrease"
                                    icon={<FiMinus />}
                                    size="md"
                                    onClick={() => handleConfigChange('max_usdt_margin', Math.max(500, tradingConfig.max_usdt_margin - 100))}
                                    variant="outline"
                                    colorScheme="green"
                                    _hover={{ transform: 'scale(1.1)' }}
                                    transition="all 0.2s"
                                    flexShrink={0}
                                  />
                                  <Center
                                    bg="rgba(0, 0, 0, 0.4)"
                                    borderWidth="1px"
                                    borderColor="rgba(255, 255, 255, 0.1)"
                                    borderRadius="md"
                                    h="10"
                                    flex="1"
                                  >
                                    <Text fontSize="lg" fontWeight="bold" color="white">
                                      {getTotalCapitalDisplayValue(tradingConfig.max_usdt_margin)}
                                    </Text>
                                  </Center>
                                  <IconButton
                                    aria-label="Increase"
                                    icon={<FiPlus />}
                                    size="md"
                                    onClick={() => handleConfigChange('max_usdt_margin', 
                                      tradingConfig.max_usdt_margin >= 999999 ? 999999 : tradingConfig.max_usdt_margin + 100)}
                                    variant="outline"
                                    colorScheme="green"
                                    _hover={{ transform: 'scale(1.1)' }}
                                    transition="all 0.2s"
                                    flexShrink={0}
                                  />
                                  <Button
                                    colorScheme="gray"
                                    variant="outline"
                                    onClick={() => handleConfigChange('max_usdt_margin', 999999)}
                                    size="md"
                                    _hover={{ transform: 'scale(1.05)' }}
                                    transition="all 0.3s"
                                    flexShrink={0}
                                    display={{ base: "none", sm: "block" }}
                                  >
                                    Unlimited
                                  </Button>
                                </HStack>
                                <Button
                                  colorScheme="gray"
                                  variant="outline"
                                  onClick={() => handleConfigChange('max_usdt_margin', 999999)}
                                  size="md"
                                  _hover={{ transform: 'scale(1.05)' }}
                                  transition="all 0.3s"
                                  w="full"
                                  display={{ base: "block", sm: "none" }}
                                >
                                  Set to Unlimited
                                </Button>
                              </VStack>
                            )}
                          </FormControl>
                          
                          {/* Per Trade USDT */}
                          <FormControl>
                            <FormLabel>
                              <HStack>
                                <Icon as={FiTarget} color="blue.400" />
                                <Text>Per Trade (USDT)</Text>
                                <Tooltip 
                                  label="Margin amount allocated for each individual trade"
                                  hasArrow
                                  placement="top"
                                  closeOnClick={true}
                                  closeOnMouseDown={true}
                                  openDelay={200}
                                  closeDelay={100}
                                  shouldWrapChildren
                                  portalProps={{ appendToParentPortal: false }}
                                  gutter={8}
                                  zIndex={9999}
                                >
                                  <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                                </Tooltip>
                              </HStack>
                            </FormLabel>
                            <HStack w="full" className="mobile-input-group">
                              <IconButton
                                aria-label="Decrease"
                                icon={<FiMinus />}
                                size="md"
                                onClick={() => handleConfigChange('per_trade_usdt', Math.max(3, tradingConfig.per_trade_usdt - 1))}
                                variant="outline"
                                colorScheme="blue"
                                _hover={{ transform: 'scale(1.1)' }}
                                transition="all 0.2s"
                                flexShrink={0}
                              />
                              <Center
                                bg="rgba(0, 0, 0, 0.4)"
                                borderWidth="1px"
                                borderColor="rgba(255, 255, 255, 0.1)"
                                borderRadius="md"
                                h="10"
                                flex="1"
                              >
                                <Text fontSize="lg" fontWeight="bold" color="white">
                                  {tradingConfig.per_trade_usdt} USDT
                                </Text>
                              </Center>
                              <IconButton
                                aria-label="Increase"
                                icon={<FiPlus />}
                                size="md"
                                onClick={() => handleConfigChange('per_trade_usdt', Math.min(500, tradingConfig.per_trade_usdt + 1))}
                                variant="outline"
                                colorScheme="blue"
                                _hover={{ transform: 'scale(1.1)' }}
                                transition="all 0.2s"
                                flexShrink={0}
                              />
                            </HStack>
                          </FormControl>
                          
                          {/* Maximum Active Trades with Unlimited option */}
                          <FormControl>
                            <FormLabel>
                              <HStack>
                                <Icon as={FiLayers} color="purple.400" />
                                <Text>Maximum Active Trades</Text>
                                <Tooltip 
                                  label="Maximum number of concurrent open positions"
                                  hasArrow
                                  placement="top"
                                  closeOnClick={true}
                                  closeOnMouseDown={true}
                                  openDelay={200}
                                  closeDelay={100}
                                  shouldWrapChildren
                                  portalProps={{ appendToParentPortal: false }}
                                  gutter={8}
                                  zIndex={9999}
                                >
                                  <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                                </Tooltip>
                              </HStack>
                            </FormLabel>
                            
                            {isMaxTradesUnlimited(tradingConfig.max_active_trades) ? (
                              <Button
                                colorScheme="purple"
                                variant="outline"
                                onClick={() => handleConfigChange('max_active_trades', 50)}
                                w="full"
                                _hover={{ transform: 'scale(1.05)' }}
                                transition="all 0.3s"
                              >
                                Set Limit
                              </Button>
                            ) : (
                              <VStack spacing={3}>
                                <HStack w="full" className="mobile-input-group">
                                  <IconButton
                                    aria-label="Decrease"
                                    icon={<FiMinus />}
                                    size="md"
                                    onClick={() => handleConfigChange('max_active_trades', Math.max(5, tradingConfig.max_active_trades - 1))}
                                    variant="outline"
                                    colorScheme="purple"
                                    _hover={{ transform: 'scale(1.1)' }}
                                    transition="all 0.2s"
                                    flexShrink={0}
                                  />
                                  <Center
                                    bg="rgba(0, 0, 0, 0.4)"
                                    borderWidth="1px"
                                    borderColor="rgba(255, 255, 255, 0.1)"
                                    borderRadius="md"
                                    h="10"
                                    flex="1"
                                  >
                                    <Text fontSize="lg" fontWeight="bold" color="white">
                                      {getMaxTradesDisplayValue(tradingConfig.max_active_trades)}
                                    </Text>
                                  </Center>
                                  <IconButton
                                    aria-label="Increase"
                                    icon={<FiPlus />}
                                    size="md"
                                    onClick={() => handleConfigChange('max_active_trades', Math.min(999, tradingConfig.max_active_trades + 1))}
                                    variant="outline"
                                    colorScheme="purple"
                                    _hover={{ transform: 'scale(1.1)' }}
                                    transition="all 0.2s"
                                    flexShrink={0}
                                  />
                                  <Button
                                    colorScheme="gray"
                                    variant="outline"
                                    onClick={() => handleConfigChange('max_active_trades', 999)}
                                    size="md"
                                    _hover={{ transform: 'scale(1.05)' }}
                                    transition="all 0.3s"
                                    flexShrink={0}
                                    display={{ base: "none", sm: "block" }}
                                  >
                                    Unlimited
                                  </Button>
                                </HStack>
                                <Button
                                  colorScheme="gray"
                                  variant="outline"
                                  onClick={() => handleConfigChange('max_active_trades', 999)}
                                  size="md"
                                  _hover={{ transform: 'scale(1.05)' }}
                                  transition="all 0.3s"
                                  w="full"
                                  display={{ base: "block", sm: "none" }}
                                >
                                  Set to Unlimited
                                </Button>
                              </VStack>
                            )}
                          </FormControl>
                        </VStack>
                        
                        <VStack spacing={6} align="stretch">
                          {/* Leverage with Enhanced Slider */}
                          <FormControl>
                            <FormLabel>
                              <HStack>
                                <Icon as={FiZap} color="orange.400" />
                                <Text>Default Leverage</Text>
                                <Tooltip 
                                  label="Leverage multiplier for position size. Higher leverage = higher risk"
                                  hasArrow
                                  placement="top"
                                  closeOnClick={true}
                                  closeOnMouseDown={true}
                                  openDelay={200}
                                  closeDelay={100}
                                  shouldWrapChildren
                                  portalProps={{ appendToParentPortal: false }}
                                  gutter={8}
                                  zIndex={9999}
                                >
                                  <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                                </Tooltip>
                              </HStack>
                            </FormLabel>
                            <VStack spacing={3}>
                              {/* Current Value Display */}
                              <Center
                                bg="rgba(0, 0, 0, 0.4)"
                                borderWidth="1px"
                                borderColor="rgba(255, 255, 255, 0.1)"
                                borderRadius="md"
                                h="12"
                                w="full"
                              >
                                <Text fontSize="xl" fontWeight="bold" color="orange.400">
                                  {tradingConfig.default_leverage}x Leverage
                                </Text>
                              </Center>
                              
                              {/* Slider with buttons */}
                              <HStack w="full" spacing={3} className="mobile-input-group">
                                <IconButton
                                  aria-label="Decrease Leverage"
                                  icon={<FiMinus />}
                                  size="md"
                                  onClick={() => handleConfigChange('default_leverage', Math.max(5, tradingConfig.default_leverage - 5))}
                                  variant="outline"
                                  colorScheme="orange"
                                  _hover={{ transform: 'scale(1.1)' }}
                                  transition="all 0.2s"
                                  flexShrink={0}
                                />
                                <Box flex="1" px={2}>
                                  <input
                                    type="range"
                                    min="5"
                                    max="100"
                                    step="5"
                                    value={tradingConfig.default_leverage}
                                    onChange={(e) => handleConfigChange('default_leverage', parseInt(e.target.value))}
                                  />
                                </Box>
                                <IconButton
                                  aria-label="Increase Leverage"
                                  icon={<FiPlus />}
                                  size="md"
                                  onClick={() => handleConfigChange('default_leverage', Math.min(100, tradingConfig.default_leverage + 5))}
                                  variant="outline"
                                  colorScheme="orange"
                                  _hover={{ transform: 'scale(1.1)' }}
                                  transition="all 0.2s"
                                  flexShrink={0}
                                />
                              </HStack>
                              
                              {/* Quick presets */}
                              <HStack w="full" justifyContent="space-between">
                                <Text fontSize="xs" color="gray.400">5x</Text>
                                <Text fontSize="xs" color="gray.400">25x</Text>
                                <Text fontSize="xs" color="gray.400">50x</Text>
                                <Text fontSize="xs" color="gray.400">75x</Text>
                                <Text fontSize="xs" color="gray.400">100x</Text>
                              </HStack>
                              
                              {/* Leverage Warning */}
                              {tradingConfig.default_leverage > 50 && (
                                <Alert status="warning" borderRadius="lg" size="sm">
                                  <AlertIcon />
                                  <AlertDescription fontSize="sm">
                                    High leverage increases potential profits but also amplifies losses.
                                  </AlertDescription>
                                </Alert>
                              )}
                            </VStack>
                          </FormControl>
                          
                          {/* Airbag Threshold */}
                          <FormControl>
                            <FormLabel>
                              <HStack>
                                <Icon as={FiShield} color="red.400" />
                                <Text>Airbag Threshold</Text>
                                <Tooltip 
                                  label="No new trades will be opened as long as the daily performance figures stay below the airbag threshold!"
                                  hasArrow
                                  placement="top"
                                  closeOnClick={true}
                                  closeOnMouseDown={true}
                                  openDelay={200}
                                  closeDelay={100}
                                  shouldWrapChildren
                                  portalProps={{ appendToParentPortal: false }}
                                  gutter={8}
                                  zIndex={9999}
                                >
                                  <Icon as={FiHelpCircle} color="gray.400" cursor="help" />
                                </Tooltip>
                              </HStack>
                            </FormLabel>
                            
                            {isAirbagOff(tradingConfig.airbag_threshold) ? (
                              <Button
                                colorScheme="green"
                                variant="outline"
                                onClick={() => handleConfigChange('airbag_threshold', 25)}
                                w="full"
                                _hover={{ transform: 'scale(1.05)' }}
                                transition="all 0.3s"
                              >
                                Enable Airbag Protection
                              </Button>
                            ) : (
                              <VStack spacing={3}>
                                <HStack w="full" className="mobile-input-group">
                                  <IconButton
                                    aria-label="Decrease"
                                    icon={<FiMinus />}
                                    size="md"
                                    onClick={() => handleConfigChange('airbag_threshold', Math.max(-25, tradingConfig.airbag_threshold - 1))}
                                    variant="outline"
                                    colorScheme="red"
                                    _hover={{ transform: 'scale(1.1)' }}
                                    transition="all 0.2s"
                                    flexShrink={0}
                                  />
                                  <Center
                                    bg="rgba(0, 0, 0, 0.4)"
                                    borderWidth="1px"
                                    borderColor="rgba(255, 255, 255, 0.1)"
                                    borderRadius="md"
                                    h="10"
                                    flex="1"
                                  >
                                    <Text fontSize="lg" fontWeight="bold" color="white">
                                      {tradingConfig.airbag_threshold}%
                                    </Text>
                                  </Center>
                                  <IconButton
                                    aria-label="Increase"
                                    icon={<FiPlus />}
                                    size="md"
                                    onClick={() => handleConfigChange('airbag_threshold', Math.min(500, tradingConfig.airbag_threshold + 1))}
                                    variant="outline"
                                    colorScheme="red"
                                    _hover={{ transform: 'scale(1.1)' }}
                                    transition="all 0.2s"
                                    flexShrink={0}
                                  />
                                  <Button
                                    colorScheme="gray"
                                    variant="outline"
                                    onClick={() => handleConfigChange('airbag_threshold', -9999)}
                                    size="md"
                                    _hover={{ transform: 'scale(1.05)' }}
                                    transition="all 0.3s"
                                    flexShrink={0}
                                    display={{ base: "none", sm: "block" }}
                                  >
                                    Disable
                                  </Button>
                                </HStack>
                                <Button
                                  colorScheme="gray"
                                  variant="outline"
                                  onClick={() => handleConfigChange('airbag_threshold', -9999)}
                                  size="md"
                                  _hover={{ transform: 'scale(1.05)' }}
                                  transition="all 0.3s"
                                  w="full"
                                  display={{ base: "block", sm: "none" }}
                                >
                                  Disable Airbag
                                </Button>
                                
                               
                              </VStack>
                            )}
                          </FormControl>
                        </VStack>
                      </Grid>
                    </CardBody>
                  </Card>
                  
                  {/* Save Button */}
                  <Center animation={`${slideUp} 0.6s ease-out 0.3s both`}>
                    <Button
                      onClick={handleSave}
                      colorScheme="green"
                      size="lg"
                      leftIcon={<FiSave />}
                      isLoading={isUpdating}
                      isDisabled={!hasUnsavedChanges}
                      bgGradient={THEME.gradients.success}
                      _hover={{ transform: "translateY(-2px)" }}
                      transition="all 0.3s"
                      px={12}
                      animation={hasUnsavedChanges ? `${glowPulse} 3s ease-in-out infinite` : 'none'}
                      w={{ base: "full", md: "auto" }}
                    >
                      Save Trading Configuration
                    </Button>
                  </Center>
                </VStack>
              </TabPanel>
              
              {/* Control Center Tab */}
              <TabPanel p={0}>
                <VStack spacing={6} align="stretch">
                  {/* Account Overview with Balance */}
            <Card
  bg='rgba(15, 27, 14, 0.8)'
  borderWidth="1px"
  borderColor={THEME.borders.primary}
  borderRadius="xl"
  backdropFilter="blur(20px)"
  overflow="hidden"
  animation={`${slideUp} 0.6s ease-out 0.1s both`}
  _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
  transition="all 0.3s"
>
  <CardHeader
    bg="rgba(0, 0, 0, 0.3)"
    borderBottomWidth="1px"
    borderColor={THEME.borders.inactive}
  >
    <HStack spacing={3}>
      <Avatar 
        size="sm" 
        bg="green.500" 
        icon={<FiDollarSign color="white" />} 
        animation={`${floating} 6s ease-in-out infinite`}
      />
      <VStack align="start" spacing={0}>
        <Heading size="md" color="white">Account Overview</Heading>
        <Text fontSize="sm" color="gray.400">Live balance and trading capacity</Text>
      </VStack>
    </HStack>
  </CardHeader>
  
  <CardBody>
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
      {/* Total Balance Stat - Direct access from API data */}
      <Stat textAlign="center">
        <StatLabel color="gray.400">Total Balance</StatLabel>
        <StatNumber color="green.400" fontSize="2xl">
          {summary?.totalBalance ? summary.totalBalance.toFixed(2) : "0.00"} USDT
        </StatNumber>
        <StatHelpText color="gray.500">
          {summary?.marginUsed && summary.marginUsed > 0 ? 
            `${parseFloat(summary.marginUsed).toFixed(2)} USDT in use` : 
            "Exchange Account"}
        </StatHelpText>
      </Stat>
      
      {/* Available Balance Stat - Direct access from API data */}
      <Stat textAlign="center">
        <StatLabel color="gray.400">Available Balance</StatLabel>
        <StatNumber color="blue.400" fontSize="2xl">
          {summary?.availableBalance ? summary.availableBalance.toFixed(2) : "0.00"} USDT
        </StatNumber>
        <StatHelpText color="gray.500">
          Status: {summary?.availableBalance && summary.availableBalance >= parseFloat(tradingConfig.per_trade_usdt) ? 
            <Text as="span" color="green.400">Ready to Trade</Text> : 
            <Text as="span" color="red.400">Insufficient Funds</Text>
          }
        </StatHelpText>
      </Stat>
      
      {/* Unrealized PnL Stat - Direct access from API data */}
     <Stat textAlign="center">
  <StatLabel color="gray.400">
    Capital Utilization
    <Tooltip 
      label="Maximum trading exposure as percentage of total balance"
      hasArrow
      placement="top"
    >
      <Box as="span" ml={1} display="inline-block">
        <Icon as={FiInfo} boxSize={3} color="gray.500" />
      </Box>
    </Tooltip>
  </StatLabel>
  <StatNumber 
    color={(() => {
      // Calculate utilization color directly
      if (!summary?.totalBalance || summary.totalBalance <= 0) return "green.400";
      
      const tradesMax = isMaxTradesUnlimited(tradingConfig.max_active_trades) ? 300 : tradingConfig.max_active_trades;
      const perTrade = parseFloat(tradingConfig.per_trade_usdt) || 10;
      const maxExposure = perTrade * tradesMax;
      const utilPct = (maxExposure / summary.totalBalance) * 100;
      
      return utilPct > 90 ? "red.400" : 
             utilPct > 70 ? "orange.400" : "green.400";
    })()}
    fontSize="2xl"
  >
    {(() => {
      // Calculate utilization directly here
      if (!summary?.totalBalance || summary.totalBalance <= 0) return "0.0";
      
      const tradesMax = isMaxTradesUnlimited(tradingConfig.max_active_trades) ? 300 : tradingConfig.max_active_trades;
      const perTrade = parseFloat(tradingConfig.per_trade_usdt) || 10;
      const maxExposure = perTrade * tradesMax;
      const utilPct = Math.min((maxExposure / summary.totalBalance) * 100, 100);
      
      return utilPct.toFixed(1);
    })()}%
  </StatNumber>
  <StatHelpText color="gray.500">
    {tradingConfig.per_trade_usdt} USDT × {isMaxTradesUnlimited(tradingConfig.max_active_trades) ? "Unlimited" : tradingConfig.max_active_trades} trades
  </StatHelpText>
</Stat>
    </SimpleGrid>
    
    {/* Enhanced Utilization Bar - Calculate based on direct values */}
    <Box mt={6}>
      <Flex justify="space-between" mb={2}>
        <Text fontSize="sm" color="gray.400">Capital Utilization</Text>
        <Text fontSize="sm" color="white" fontWeight="bold">
          {(() => {
            // Calculate utilization directly here
            if (!summary?.totalBalance || summary.totalBalance <= 0) return "0.0%";
            
            const tradesMax = isMaxTradesUnlimited(tradingConfig.max_active_trades) ? 300 : tradingConfig.max_active_trades;
            const perTrade = parseFloat(tradingConfig.per_trade_usdt) || 10;
            const maxExposure = perTrade * tradesMax;
            const utilPct = Math.min((maxExposure / summary.totalBalance) * 100, 100);
            
            return utilPct.toFixed(1) + "%";
          })()}
        </Text>
      </Flex>
      <Box
        bg="rgba(255, 255, 255, 0.1)"
        borderRadius="full"
        h="4"
        overflow="hidden"
        position="relative"
      >
        <Box
          bg={(() => {
            // Calculate utilization color directly
            if (!summary?.totalBalance || summary.totalBalance <= 0) return "green.500";
            
            const tradesMax = isMaxTradesUnlimited(tradingConfig.max_active_trades) ? 300 : tradingConfig.max_active_trades;
            const perTrade = parseFloat(tradingConfig.per_trade_usdt) || 10;
            const maxExposure = perTrade * tradesMax;
            const utilPct = (maxExposure / summary.totalBalance) * 100;
            
            return utilPct > 90 ? "red.500" : 
                   utilPct > 70 ? "orange.500" : "green.500";
          })()}
          h="full"
          w={(() => {
            // Calculate utilization width directly
            if (!summary?.totalBalance || summary.totalBalance <= 0) return "0%";
            
            const tradesMax = isMaxTradesUnlimited(tradingConfig.max_active_trades) ? 300 : tradingConfig.max_active_trades;
            const perTrade = parseFloat(tradingConfig.per_trade_usdt) || 10;
            const maxExposure = perTrade * tradesMax;
            const utilPct = Math.min((maxExposure / summary.totalBalance) * 100, 100);
            
            return utilPct.toFixed(1) + "%";
          })()}
          borderRadius="full"
          transition="all 0.5s ease"
          animation={(() => {
            // Calculate animation based on utilization
            if (!summary?.totalBalance || summary.totalBalance <= 0) return "none";
            
            const tradesMax = isMaxTradesUnlimited(tradingConfig.max_active_trades) ? 300 : tradingConfig.max_active_trades;
            const perTrade = parseFloat(tradingConfig.per_trade_usdt) || 10;
            const maxExposure = perTrade * tradesMax;
            const utilPct = (maxExposure / summary.totalBalance) * 100;
            
            return utilPct > 90 ? `${glowPulse} 2s ease-in-out infinite` : 'none';
          })()}
        />
      </Box>
    </Box>
  </CardBody>
</Card>
                  {/* Enhanced Trading Status Grid */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {/* Trading Engine - Enhanced with more details */}
                    <Card
  bg={tradingConfig.enable_trading ? 
      THEME.glass.accent : 
      THEME.glass.primary}
  borderWidth="1px"
  borderColor={tradingConfig.enable_trading ? 
      THEME.borders.active : 
      THEME.borders.primary}
  borderRadius="xl"
  backdropFilter="blur(20px)"
  overflow="hidden"
  animation={tradingConfig.enable_trading ? 
      `${floating} 3s ease-in-out infinite, ${slideUp} 0.6s ease-out 0.2s both` : 
      `${slideUp} 0.6s ease-out 0.2s both`}
  _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
  transition="all 0.3s"
>
  <CardBody textAlign="center">
    <VStack spacing={4}>
      <Avatar
        size="xl"
        bg={tradingConfig.enable_trading ? "green.500" : "gray.500"}
        icon={
          <Icon 
            as={tradingConfig.enable_trading ? FiPlay : FiPause} 
            boxSize={10} 
            color="white" 
          />
        }
        animation={tradingConfig.enable_trading ? `${floating} 3s ease-in-out infinite` : 'none'}
      />
      
      <VStack spacing={2}>
        <Heading size="lg" color="white">Trading Engine</Heading>
        <Badge
          px={4}
          py={1}
          borderRadius="full"
          colorScheme={tradingConfig.enable_trading ? "green" : "gray"}
          fontSize="md"
          fontWeight="bold"
         
        >
          {tradingConfig.enable_trading ? "ACTIVELY TRADING" : "STANDBY MODE"}
        </Badge>
        <Text fontSize="sm" color="gray.400" textAlign="center">
          {tradingConfig.enable_trading ? 
            "AI is monitoring markets and executing trades" :
            "Ready to start automated trading"
          }
        </Text>
      </VStack>
      
      <Button
        colorScheme={tradingConfig.enable_trading ? "red" : "green"}
        onClick={handleTradingToggle}
        leftIcon={<Icon as={tradingConfig.enable_trading ? FiPause : FiPlay} />}
        size="lg"
        isDisabled={!isConnected || !isVerified}
        isLoading={isUpdating}
        _hover={{ transform: "translateY(-2px)", boxShadow: 'lg' }}
        transition="all 0.2s"
        w="full"
      >
        {tradingConfig.enable_trading ? "Stop Trading" : "Start Trading"}
      </Button>
      
      {/* Trading Requirements */}
      {(!isConnected || !isVerified) && (
        <Alert status="warning" borderRadius="lg" size="sm">
          <AlertIcon />
          <AlertDescription fontSize="xs">
            API connection and verification required
          </AlertDescription>
        </Alert>
      )}
    </VStack>
  </CardBody>
</Card>
                    
                    {/* Current Strategy - Enhanced design */}
                    <Card
                      bg='rgba(15, 27, 14, 0.8)'
                      borderWidth="1px"
                      borderColor={THEME.borders.primary}
                      borderRadius="xl"
                      backdropFilter="blur(20px)"
                      overflow="hidden"
                      animation={`${slideUp} 0.6s ease-out 0.3s both`}
                      _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
                      transition="all 0.3s"
                    >
                      <CardBody>
                        <VStack spacing={6}>
                          <HStack w="full" justify="space-between" align="center">
                            <Icon as={FiCpu} color="blue.400" boxSize={10} animation={`${floating} 4s ease-in-out infinite`} />
                            <Heading size="lg" color="white">Strategy</Heading>
                          </HStack>
                          
                          <VStack spacing={4} w="full">
                            <Flex justify="space-between" w="full" align="center">
                              <Text fontSize="md" color="gray.400">AI Model</Text>
                              <Badge 
                                colorScheme={
                                  tradingConfig.trade_type === '4' ? 'blue' :
                                  tradingConfig.trade_type === '8' ? 'purple' : 'green'
                                }
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontSize="sm"
                                fontWeight="bold"
                                textTransform="uppercase"
                              >
                                {tradingConfig.trade_type === '4' ? 'MODEL 4' :
                                 tradingConfig.trade_type === '8' ? 'MODEL 8' : 'AUTO MODEL'}
                              </Badge>
                            </Flex>
                            
                            <Flex justify="space-between" w="full" align="center">
                              <Text fontSize="md" color="gray.400">Direction</Text>
                              <Badge 
                                colorScheme={
                                  tradingConfig.trade_side === '0' ? 'blue' :
                                  tradingConfig.trade_side === '1' ? 'green' : 'red'
                                }
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontSize="sm"
                                fontWeight="bold"
                                textTransform="uppercase"
                              >
                                {tradingConfig.trade_side === '0' ? 'BOTH DIRECTIONS' :
                                 tradingConfig.trade_side === '1' ? 'LONG ONLY' : 'SHORT ONLY'}
                              </Badge>
                            </Flex>
                            
                            <Flex justify="space-between" w="full" align="center">
                              <Text fontSize="md" color="gray.400">Leverage</Text>
                              <Badge 
                                colorScheme={
                                  tradingConfig.default_leverage <= 20 ? "green" :
                                  tradingConfig.default_leverage <= 50 ? "orange" : "red"
                                }
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontSize="sm"
                                fontWeight="bold"
                                textTransform="uppercase"
                              >
                                {tradingConfig.default_leverage}X
                              </Badge>
                            </Flex>
                            
                            <Flex justify="space-between" w="full" align="center">
                              <Text fontSize="md" color="gray.400">Airbag</Text>
                              <Badge 
                                colorScheme={isAirbagOff(tradingConfig.airbag_threshold) ? "gray" : "red"}
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontSize="sm"
                                fontWeight="bold"
                                textTransform="uppercase"
                              >
                                {getAirbagDisplayValue(tradingConfig.airbag_threshold)}
                              </Badge>
                            </Flex>
                          </VStack>
                        </VStack>
                      </CardBody>
                    </Card>
                    
                    {/* System Status - Enhanced design */}
                   <Card
  bg='rgba(15, 27, 14, 0.8)'
  borderWidth="1px"
  borderColor={THEME.borders.primary}
  borderRadius="xl"
  backdropFilter="blur(20px)"
  overflow="hidden"
  animation={`${slideUp} 0.6s ease-out 0.4s both`}
  _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
  transition="all 0.3s"
>
  <CardBody>
    <VStack spacing={6}>
      <HStack w="full" justify="space-between" align="center">
        <Icon as={FiActivity} color="purple.400" boxSize={10} animation={`${floating} 5s ease-in-out infinite`} />
        <Heading size="lg" color="white">System</Heading>
      </HStack>
      
      <VStack spacing={4} w="full">
        {/* API Status */}
        <Flex justify="space-between" w="full" align="center">
          <Text fontSize="md" color="gray.400">API Status</Text>
          <Badge 
            colorScheme={isConnected ? "green" : "red"}
            borderRadius="full"
            px={3}
            py={1}
            fontSize="sm"
            fontWeight="bold"
            textTransform="uppercase"
          >
            {isConnected ? "CONNECTED" : "DISCONNECTED"}
          </Badge>
        </Flex>
        
        {/* Verification */}
        <Flex justify="space-between" w="full" align="center">
          <Text fontSize="md" color="gray.400">Verification</Text>
          <Badge 
            colorScheme={isVerified ? "green" : "orange"}
            borderRadius="full"
            px={3}
            py={1}
            fontSize="sm"
            fontWeight="bold"
            textTransform="uppercase"
          >
            {isVerified ? "VERIFIED" : "PENDING"}
          </Badge>
        </Flex>
        
        {/* Trading Status - Shows if trading is enabled */}
        <Flex justify="space-between" w="full" align="center">
          <Text fontSize="md" color="gray.400">Trading Status</Text>
          <Badge 
            colorScheme={tradingConfig.enable_trading ? "green" : "gray"}
            borderRadius="full"
            px={3}
            py={1}
            fontSize="sm"
            fontWeight="bold"
            textTransform="uppercase"
          >
            {tradingConfig.enable_trading ? "ACTIVE" : "STANDBY"}
          </Badge>
        </Flex>
        
        {/* Current PnL - Display unrealized PnL with color based on positive/negative */}
      <Flex justify="space-between" w="full" align="center">
  <Text fontSize="md" color="gray.400">Current PnL</Text>
  <Badge 
    colorScheme={summary?.unrealizedPnl >= 0 ? "green" : "red"}
    borderRadius="full"
    px={3}
    py={1}
    fontSize="sm"
    fontWeight="bold"
  >
    {summary?.unrealizedPnl >= 0 ? "+" : ""}
    {parseFloat(summary?.unrealizedPnl || 0).toFixed(2)} USDT
  </Badge>
</Flex>

<Flex justify="space-between" w="full" align="center">
  <Text fontSize="md" color="gray.400">Open Positions</Text>
  <Badge 
    colorScheme={
      stats?.totalOpen > 0 ? "blue" : "gray"
    }
    borderRadius="full"
    px={3}
    py={1}
    fontSize="sm"
    fontWeight="bold"
  >
    {stats?.totalOpen || 0}
  </Badge>
</Flex>
      </VStack>
    </VStack>
  </CardBody>
</Card>
                  </SimpleGrid>
                  
                  {/* Enhanced Quick Actions */}
                  <Card
                    bg='rgba(15, 27, 14, 0.8)'
                    borderWidth="1px"
                    borderColor={THEME.borders.primary}
                    borderRadius="xl"
                    backdropFilter="blur(20px)"
                    overflow="hidden"
                    animation={`${slideUp} 0.6s ease-out 0.5s both`}
                    _hover={{ transform: 'translateY(-5px)', boxShadow: THEME.shadows.quantum }}
                    transition="all 0.3s"
                  >
                    <CardBody>
                      <VStack spacing={6}>
                        <Heading size="md" color="white" textAlign="center">Quick Actions</Heading>
                        
                        <Stack direction={{ base: "column", md: "row" }} spacing={6} w="full">
                          <Button
                            onClick={handleSave}
                            colorScheme="green"
                            leftIcon={<FiSave />}
                            isLoading={isUpdating}
                            isDisabled={!hasUnsavedChanges}
                            size="lg"
                            _hover={{ transform: "translateY(-2px)", boxShadow: '0 10px 25px rgba(34, 197, 94, 0.4)' }}
                            transition="all 0.2s"
                            animation={hasUnsavedChanges ? `${glowPulse} 3s ease-in-out infinite` : 'none'}
                            flex="1"
                          >
                            Save All Changes
                          </Button>
                          
                          <Button
                            onClick={handleTestConnection}
                            colorScheme="blue"
                            leftIcon={<FiCheckCircle />}
                            isLoading={isChecking}
                            isDisabled={!isConnected}
                            size="lg"
                            variant="outline"
                            _hover={{ transform: "translateY(-2px)", boxShadow: '0 10px 25px rgba(59, 130, 246, 0.4)' }}
                            transition="all 0.2s"
                            flex="1"
                          >
                            Test API Connection
                          </Button>
                          
                          <Button
                            onClick={() => {
                              refetchDashboard();
                              toast({
                                title: "Balance Refreshed",
                                description: "Account balance has been updated from exchange",
                                status: "success",
                                duration: 3000,
                                isClosable: true,
                              });
                            }}
                            colorScheme="purple"
                            leftIcon={<FiRefreshCw />}
                            size="lg"
                            variant="outline"
                            _hover={{ transform: "translateY(-2px)", boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)' }}
                            transition="all 0.2s"
                            flex="1"
                          >
                            Refresh Balance
                          </Button>
                        </Stack>
                        
                        {/* System Health Summary */}
                        <Box p={4} bg="rgba(0, 0, 0, 0.3)" borderRadius="lg" w="full">
                          <Text fontSize="sm" color="gray.400" textAlign="center" mb={3}>
                            System Health Summary
                          </Text>
                          <HStack justify="space-around" wrap="wrap">
                            <VStack spacing={1}>
                              <Text fontSize="xs" color="gray.500">Connection</Text>
                              <Icon 
                                as={isConnected && isVerified ? FiCheckCircle : FiAlertTriangle} 
                                color={isConnected && isVerified ? "green.400" : "orange.400"}
                                boxSize={5}
                              />
                            </VStack>
                            <VStack spacing={1}>
                              <Text fontSize="xs" color="gray.500">Trading</Text>
                              <Icon 
                                as={tradingConfig.enable_trading ? FiPlay : FiPause} 
                                color={tradingConfig.enable_trading ? "green.400" : "gray.400"}
                                boxSize={5}
                              />
                            </VStack>
                            <VStack spacing={1}>
                              <Text fontSize="xs" color="gray.500">Balance</Text>
                              <Icon 
                                as={tradeCapacity.canTrade ? FiCheckCircle : FiAlertTriangle} 
                                color={tradeCapacity.canTrade ? "green.400" : "orange.400"}
                                boxSize={5}
                              />
                            </VStack>
                            <VStack spacing={1}>
                              <Text fontSize="xs" color="gray.500">Protection</Text>
                              <Icon 
                                as={isAirbagOff(tradingConfig.airbag_threshold) ? FiX : FiShield} 
                                color={isAirbagOff(tradingConfig.airbag_threshold) ? "gray.400" : "green.400"}
                                boxSize={5}
                              />
                            </VStack>
                          </HStack>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>
    </>
  );
};

export default ApiSettings;